import { create } from "zustand";
import { persist } from "zustand/middleware";

interface DismissibleWarningsStore {
  dismissedWarnings: Set<string>;
  isDismissed: (warningId: string) => boolean;
  dismissWarning: (warningId: string) => void;
  resetWarning: (warningId: string) => void;
  resetAllWarnings: () => void;
}

// Type for the persisted state (array instead of Set)
interface PersistedDismissibleWarningsState {
  dismissedWarnings?: string[];
}

export const useDismissibleWarningsStore = create<DismissibleWarningsStore>()(
  persist(
    (set, get) => ({
      dismissedWarnings: new Set<string>(),

      isDismissed: (warningId: string) => {
        const { dismissedWarnings } = get();
        return dismissedWarnings.has(warningId);
      },

      dismissWarning: (warningId: string) => {
        const { dismissedWarnings } = get();
        const newDismissedWarnings = new Set(dismissedWarnings);
        newDismissedWarnings.add(warningId);

        set({
          dismissedWarnings: newDismissedWarnings,
        });
      },

      resetWarning: (warningId: string) => {
        const { dismissedWarnings } = get();
        const newDismissedWarnings = new Set(dismissedWarnings);
        newDismissedWarnings.delete(warningId);

        set({
          dismissedWarnings: newDismissedWarnings,
        });
      },

      resetAllWarnings: () => {
        set({
          dismissedWarnings: new Set<string>(),
        });
      },
    }),
    {
      name: "dismissed-warnings",
      partialize: (state) => ({
        dismissedWarnings: Array.from(state.dismissedWarnings),
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as PersistedDismissibleWarningsState;
        const result = { ...currentState };

        if (persisted?.dismissedWarnings && Array.isArray(persisted.dismissedWarnings)) {
          result.dismissedWarnings = new Set(persisted.dismissedWarnings);
        }

        return result;
      },
    },
  ),
);
